import React, { useState, useEffect, useRef } from "react";

const PROMPTS = [
  "The portal sucks! Tell us what you'd change.",
  "Why do we even use this? Tell us what to do better!",
  "If you could burn one feature and rebuild it, which one?",
  "What makes you want to throw your laptop out the window?",
  "Be honest — what's the worst thing about this portal?",
  "We know it's rough. Help us make it less rough.",
  "Got a gripe? We're all ears. Seriously.",
];

const BANNER_HEIGHT = 33; // px — keep in sync with padding/fontSize
const STORAGE_KEY = "feedback_banner_dismissed";
const FEATUREBASE_ORG = "deskbook";
const FEATUREBASE_BOARD = "69a5de4f6c9a9e836a25add7";

function isDismissedToday() {
  const dismissed = localStorage.getItem(STORAGE_KEY);
  if (!dismissed) return false;
  const today = new Date().toDateString();
  return dismissed === today;
}

function getRandomPrompt() {
  return PROMPTS[Math.floor(Math.random() * PROMPTS.length)];
}

export default function FeedbackBanner() {
  const [visible, setVisible] = useState(false);
  const promptRef = useRef(getRandomPrompt());
  const sdkInitialised = useRef(false);

  useEffect(() => {
    if (!isDismissedToday()) {
      setVisible(true);
    }
  }, []);

  // Push the entire page down when banner is visible
  useEffect(() => {
    if (visible) {
      document.body.style.paddingTop = BANNER_HEIGHT + "px";
    }
    return () => {
      document.body.style.paddingTop = "";
    };
  }, [visible]);

  useEffect(() => {
    if (sdkInitialised.current) return;
    sdkInitialised.current = true;

    // Create the Featurebase command queue
    if (typeof window.Featurebase !== "function") {
      const queue = [];
      const fb = (...args) => {
        queue.push(args);
      };
      fb.q = queue;
      window.Featurebase = fb;
    }

    // Load the SDK script
    if (!document.getElementById("featurebase-sdk")) {
      const script = document.createElement("script");
      script.id = "featurebase-sdk";
      script.src = "https://do.featurebase.app/js/sdk.js";
      script.async = true;
      document.head.appendChild(script);
    }

    // Initialise the feedback widget
    window.Featurebase("initialize_feedback_widget", {
      organization: FEATUREBASE_ORG,
      placement: "right",
      theme: "light",
      defaultBoard: FEATUREBASE_BOARD,
    });
  }, []);

  function handleDismiss() {
    localStorage.setItem(STORAGE_KEY, new Date().toDateString());
    setVisible(false);
  }

  function handleFeedback() {
    window.postMessage({
      target: "FeaturebaseWidget",
      data: { action: "openFeedbackWidget" },
    });
  }

  if (!visible) return null;

  return (
    <div style={styles.banner}>
      <span style={styles.prompt}>{promptRef.current}</span>
      <div style={styles.actions}>
        <button style={styles.feedbackBtn} onClick={handleFeedback}>
          Give Feedback
        </button>
        <button
          style={styles.closeBtn}
          onClick={handleDismiss}
          aria-label="Dismiss feedback banner"
        >
          ×
        </button>
      </div>
    </div>
  );
}

const styles = {
  banner: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 98, // above the fixed header (z-index: 97)
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 16px",
    backgroundColor: "#f4f5f7",
    borderBottom: "1px solid #e8e8e8",
    fontSize: "13px",
    color: "#5e6c84",
    lineHeight: "1.4",
    height: BANNER_HEIGHT + "px",
    boxSizing: "border-box",
  },
  prompt: {
    flex: 1,
    marginRight: "12px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  feedbackBtn: {
    background: "none",
    border: "1px solid #c1c7d0",
    borderRadius: "4px",
    padding: "3px 10px",
    fontSize: "12px",
    color: "#42526e",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "16px",
    color: "#97a0af",
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: "1",
  },
};
